/// <amd-module name="Repos/Views/Branches/Components/Columns/BranchColumns" />
import * as React from "react";
import { GitBranchStats, GitRef, GitRefFavorite } from "Repos/Common/Contracts/Git";
import { GitRepositoryPermissions } from "Repos/Common/Permissions";
import { branchNameToRef, refToBranchName } from "Repos/Common/Util/Ref";
import { PipelineStatus } from "Repos/PipelineStatus/Components/PipelineStatus";
import { SparseTreeNode } from "Repos/SparseTree/SparseTreeProvider.Types";
import * as Resources from "Repos/Views/Branches/Resources";
import {
    IObservableLikeValue,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableCollection,
    ObservableLike,
    ObservableValue
} from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { css } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Icon } from "VSSUI/Icon";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import { ColumnMore, ITableBreakpoint, ITableColumn, renderLoadingCell, SimpleTableCell, Table, TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, Tree } from "VSSUI/TreeEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { VssPersona } from "VSSUI/VssPersona";
import { EnhancedGitRef } from "../../Data/EnhancedGitRef";
import { FavoriteStarButton, FavoriteStarStatic } from "../Favorite/FavoriteStarButton";
import { ClipboardButton } from "VSSUI/Clipboard"
import * as ClipboardResources from "VSSUI/Resources.Clipboard";

export declare type UniversalBranchColumn = ITreeColumn<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>> & ITableColumn<EnhancedGitRef>;
export declare type UniversalBranchColumnContent =
    | ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>>
    | IObservableLikeValue<EnhancedGitRef>;

interface ColumnGitRef {
    gitRef?: EnhancedGitRef;
    longName: string;
    shortName: string;
}

function isTreeItem(columnContent: UniversalBranchColumnContent): columnContent is ITreeItemEx<IObservableLikeValue<SparseTreeNode<EnhancedGitRef>>> {
    return (columnContent as ITreeItemEx<any>).underlyingItem !== undefined;
}

function getColumnGitRef(columnContent: UniversalBranchColumnContent): ColumnGitRef {
    if (isTreeItem(columnContent)) {
        const sparseNode = ObservableLike.getValue(columnContent.underlyingItem.data);

        return {
            gitRef: sparseNode.kind === "item" ? sparseNode.data : undefined,
            longName: sparseNode.absoluteIdentifier,
            shortName: sparseNode.relativeIdentifier
        };
    } else {
        const unpackedObservable = ObservableLike.getValue(columnContent);
        const branchName = refToBranchName(unpackedObservable.rootRef.name);

        return {
            gitRef: unpackedObservable,
            longName: branchName,
            shortName: branchName
        };
    }
}

// name, commit, author, author date, behind/ahead, status, PR, favorite
export const StandardBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.xsmall,
        columnWidths: [-100, 0, 0, 0, 0, 0, 0, 0]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [-30, -10, -25, -15, 120, -10, -10, 38]
    },
    {
        breakpoint: ScreenBreakpoints.medium,
        columnWidths: [-30, -10, -25, -15, 120, -10, -10, 38]
    },
    {
        breakpoint: ScreenBreakpoints.large,
        columnWidths: [-35, -10, -20, -15, 120, -10, -10, 38]
    },
    {
        breakpoint: ScreenBreakpoints.xlarge,
        columnWidths: [-45, -10, -15, -10, 120, -10, -10, 38]
    }
];

export function NameColumn(pathHasPolicy?: (path: string) => boolean, getBranchPolicyUrl?: (branchName: string) => string): UniversalBranchColumn {
    return {
        id: "name",
        columnLayout: TableColumnLayout.singleLinePrefix,
        name: Resources.ColumnBranchHeader,
        hierarchical: true,
        width: new ObservableValue(-100),
        renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
            const columnGitRef = getColumnGitRef(treeItem);

            const hasPolicy = pathHasPolicy && pathHasPolicy(columnGitRef.longName);
            let contents: JSX.Element;

            if (columnGitRef.gitRef) {
                const gitRef = columnGitRef.gitRef;

                contents = (
                    <>
                        <Tooltip text={columnGitRef.longName}>
                            <FPSLink className="icon-margin bolt-table-link text-ellipsis" href={gitRef.refUrl} removeUnderline={true}>
                                {columnGitRef.shortName}
                            </FPSLink>
                        </Tooltip>

                        <ClipboardButton
                            className="branch-copy-button"
                            getContent={() => columnGitRef.longName}
                            key="clipboard"
                            showCopiedTooltip={true}
                            tooltipProps={{ text: ClipboardResources.CopyToClipboard }}
                        />
            
                        {gitRef.rootRef.isLocked && (
                            <Icon
                                className="icon-margin"
                                iconName="Lock"
                                tooltipProps={{
                                    text: format(Resources.LockedBy, gitRef.rootRef.isLockedBy.displayName)
                                }}
                            />
                        )}
                        {hasPolicy && (
                            <FPSLink ariaLabel={Resources.BranchHasPolicies} href={gitRef.policiesUrl}>
                                <Icon
                                    className="icon-margin"
                                    iconName="Ribbon"
                                    tooltipProps={{
                                        text: Resources.BranchHasPolicies
                                    }}
                                />
                            </FPSLink>
                        )}
                        <PillGroup className={"repos-branch-pill-group"}>
                            {gitRef.isDefault && (
                                <Pill excludeTabStop={true} size={PillSize.compact}>
                                    {Resources.DefaultBranch}
                                </Pill>
                            )}
                            {gitRef.isCompare && (
                                <Pill excludeTabStop={true} size={PillSize.compact}>
                                    {Resources.CompareBranch}
                                </Pill>
                            )}
                        </PillGroup>
                    </>
                );
            } else {
                const icon = (
                    <Icon
                        iconName="Ribbon"
                        tooltipProps={{
                            text: Resources.BranchHasPolicies
                        }}
                    />
                );

                contents = (
                    <>
                        <Tooltip text={columnGitRef.longName}>
                            <span className="icon-margin">{columnGitRef.shortName}</span>
                        </Tooltip>
                        {hasPolicy && (getBranchPolicyUrl ? <FPSLink href={getBranchPolicyUrl(columnGitRef.longName + "/*")} removeUnderline={true}>{icon}</FPSLink> : icon)}
                    </>
                );
            }

            const isFolder = columnGitRef.gitRef === undefined;

            if (isTreeItem(treeItem)) {
                return (
                    <ExpandableTreeCell
                        contentClassName={css(!isFolder && "bolt-table-cell-content-with-link")}
                        colspan={isFolder ? 7 : undefined}
                        columnIndex={columnIndex}
                        key={columnIndex}
                        treeColumn={tableColumn}
                        treeItem={treeItem}
                    >
                        <Icon
                            className={css("icon-margin", isFolder && "repos-folder-icon")}
                            iconName={isFolder ? "FabricFolderFill" : "OpenSource"}
                        />
                        {contents}
                    </ExpandableTreeCell>
                );
            } else {
                return (
                    <SimpleTableCell
                        contentClassName={css(!isFolder && "bolt-table-cell-content-with-link")}
                        colspan={isFolder ? 7 : undefined}
                        columnIndex={columnIndex}
                        key={columnIndex}
                        tableColumn={tableColumn}
                    >
                        <Icon
                            className={css("icon-margin", isFolder && "repos-folder-icon")}
                            iconName={isFolder ? "FabricFolderFill" : "OpenSource"}
                        />
                        {contents}
                    </SimpleTableCell>
                );
            }
        }
    };
}

export const CommitColumn: UniversalBranchColumn = {
    id: "commit",
    name: Resources.ColumnCommitHeader,
    width: new ObservableValue(-100),
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
        const columnGitRef = getColumnGitRef(treeItem);

        if (columnGitRef.gitRef) {
            const gitRef = columnGitRef.gitRef;

            let contents: JSX.Element = <></>;

            if (gitRef.metadata) {
                const shortCommitId = gitRef.metadata.commitId.substring(0, 8);
                contents = (
                    <FPSLink className="bolt-table-link monospaced-m" href={gitRef.commitUrl}>
                        {shortCommitId}
                    </FPSLink>
                );

                if (gitRef.metadata.comment) {
                    contents = <Tooltip text={shortCommitId + ": " + gitRef.metadata.comment}>{contents}</Tooltip>;
                }
            }

            return (
                <SimpleTableCell
                    contentClassName="bolt-table-cell-content-with-link"
                    key={columnIndex}
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                >
                    {contents}
                </SimpleTableCell>
            );
        }
        return <React.Fragment key={columnIndex} />;
    }
};

export const AuthorColumn: UniversalBranchColumn = {
    id: "author",
    name: Resources.ColumnAuthorHeader,
    width: new ObservableValue(-100),
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
        const columnGitRef = getColumnGitRef(treeItem);

        if (columnGitRef.gitRef && columnGitRef.gitRef.metadata) {
            const gitRef = columnGitRef.gitRef;
            return (
                <SimpleTableCell key={columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                    {gitRef.metadata && (
                        <>
                            <VssPersona
                                className="icon-margin"
                                displayName={gitRef.metadata.author.name}
                                imageUrl={gitRef.metadata.author.imageUrl}
                                size={"extra-small-plus"}
                            />
                            <Tooltip overflowOnly={true}>
                                <span className="text-ellipsis">{gitRef.metadata.author.name}</span>
                            </Tooltip>
                        </>
                    )}
                </SimpleTableCell>
            );
        } else {
            return <React.Fragment key={columnIndex} />;
        }
    }
};

export const AuthoredDateColumn: UniversalBranchColumn = {
    id: "authoredDate",
    name: Resources.ColumnAuthoredDateHeader,
    width: new ObservableValue(-100),
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
        const columnGitRef = getColumnGitRef(treeItem);

        if (columnGitRef.gitRef) {
            const gitRef = columnGitRef.gitRef;

            return (
                <SimpleTableCell key={columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                    {gitRef.metadata && <Ago date={gitRef.metadata.author.date} format={AgoFormat.Compact} />}
                </SimpleTableCell>
            );
        }

        return <React.Fragment key={columnIndex} />;
    }
};

export function BehindAheadColumn(compareBranch: EnhancedGitRef | undefined): UniversalBranchColumn {
    return {
        id: "behindAhead",
        name: Resources.ColumnBehindAheadHeader,
        width: new ObservableValue(120),
        renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
            const columnGitRef = getColumnGitRef(treeItem);

            if (columnGitRef.gitRef) {
                const gitRef = columnGitRef.gitRef;

                return (
                    <SimpleTableCell
                        contentClassName="bolt-table-cell-content-with-link"
                        key={columnIndex}
                        columnIndex={columnIndex}
                        tableColumn={tableColumn}
                    >
                        {!gitRef.isCompare && (
                            <Observer stats={gitRef.stats}>
                                {(observerProps: { stats: GitBranchStats | undefined }) => {
                                    return observerProps.stats ? (
                                        <BehindAhead
                                            className="flex-grow"
                                            compareBranch={compareBranch}
                                            compareUrl={gitRef.compareUrl}
                                            reverseCompareUrl={gitRef.reverseCompareUrl}
                                            stats={observerProps.stats}
                                        />
                                    ) : (
                                        renderLoadingCell(TableColumnLayout.singleLine)
                                    );
                                }}
                            </Observer>
                        )}
                    </SimpleTableCell>
                );
            }

            return <React.Fragment key={columnIndex} />;
        }
    };
}

export const StatusColumn: UniversalBranchColumn = {
    id: "status",
    name: Resources.ColumnStatusHeader,
    width: new ObservableValue(-100),
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
        const columnGitRef = getColumnGitRef(treeItem);

        if (columnGitRef.gitRef) {
            const gitRef = columnGitRef.gitRef;

            return (
                <SimpleTableCell
                    key={columnIndex}
                    columnIndex={columnIndex}
                    contentClassName="bolt-table-cell-content-with-link"
                    tableColumn={tableColumn}
                >
                    <PipelineStatus className="bolt-table-link" statuses={gitRef.rootRef.statuses} />
                </SimpleTableCell>
            );
        }

        return <React.Fragment key={columnIndex} />;
    }
};

export const PullRequestColumn: UniversalBranchColumn = {
    id: "pullRequest",
    name: Resources.ColumnPullRequestHeader,
    width: new ObservableValue(-100),
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
        const columnGitRef = getColumnGitRef(treeItem);

        if (columnGitRef.gitRef) {
            const gitRef = columnGitRef.gitRef;
            let contents: JSX.Element = <React.Fragment />;

            if (gitRef.pullRequestUrl && gitRef.pullRequest) {
                contents = (
                    <FPSLink className="flex-row flex-center bolt-table-link" href={gitRef.pullRequestUrl} removeUnderline={true}>
                        <Icon iconName="BranchPullRequest" className="icon-margin" />
                        {gitRef.pullRequest.pullRequestId}
                    </FPSLink>
                );
            }
            return (
                <SimpleTableCell
                    contentClassName="bolt-table-cell-content-with-link"
                    key={columnIndex}
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                >
                    {contents}
                </SimpleTableCell>
            );
        }

        return <React.Fragment key={columnIndex} />;
    }
};


export function FavoriteColumn(
    onSetFavoriteBranch: (branch: EnhancedGitRef, isFavorite: boolean) => void,
    onSetFavoriteFolder?: (path: string, isFavorite: boolean) => void,
    getFolderFavoriteObservable?: (path: string) => IReadonlyObservableValue<GitRefFavorite | undefined>
): UniversalBranchColumn {
    return {
        id: "favorite",
        name: "",
        width: new ObservableValue(42), // 32 for button + 8 padding from .bolt-table-cell-side-action + 2 for padding from <td>
        renderCell: (rowIndex: number, columnIndex: number, tableColumn: UniversalBranchColumn, treeItem: UniversalBranchColumnContent) => {
            const columnGitRef = getColumnGitRef(treeItem);

            let contents = <></>;
            if (columnGitRef.gitRef) {
                const gitRef = columnGitRef.gitRef;

                if (gitRef.isDefault) {
                    contents = <FavoriteStarStatic />;
                } else {
                    const isFavorite = columnGitRef.gitRef.favorite ? true : false;
                    contents = (
                        <FavoriteStarButton
                            className={css(!isFavorite && "bolt-table-cell-content-reveal")}
                            onClick={event => {
                                onSetFavoriteBranch(gitRef, !isFavorite);
                                event.preventDefault();
                            }}
                            isFavorite={isFavorite}
                        />
                    );
                }
            } else if (onSetFavoriteFolder && getFolderFavoriteObservable) {
                const favoriteData = getFolderFavoriteObservable(columnGitRef.longName);

                contents = (
                    <Observer favoriteData={favoriteData}>
                        {(observerProps: { favoriteData: GitRefFavorite | undefined }) => (
                            <FavoriteStarButton
                                className={css(observerProps.favoriteData === undefined && "bolt-table-cell-content-reveal")}
                                onClick={event => {
                                    onSetFavoriteFolder(columnGitRef.longName, observerProps.favoriteData === undefined ? true : false);
                                    event.preventDefault();
                                }}
                                isFavorite={observerProps.favoriteData !== undefined}
                            />
                        )}
                    </Observer>
                );
            }

            return (
                <SimpleTableCell
                    contentClassName={"justify-center bolt-table-cell-content-with-button"}
                    key={columnIndex}
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                >
                    {contents}
                </SimpleTableCell>
            );
        }
    };
}

export function ActionsColumn(
    addContributedMenuItems: (branchName: string, item: EnhancedGitRef | undefined, collection: IReadonlyObservableArray<IMenuItem>) => void,
    isUserMember: boolean,
    onCheckPermission: (permission: GitRepositoryPermissions, gitRef?: GitRef) => boolean,
    onCreate: (enhancedRef: EnhancedGitRef, onDismiss: () => void) => void,
    onDelete: (enhancedRef: EnhancedGitRef, onDismiss: () => void) => void,
    onLock: (enhancedRef: EnhancedGitRef) => void,
    onUnlock: (enhancedRef: EnhancedGitRef) => void,
    onSetAsCompare: (enhancedRef: EnhancedGitRef) => void,
    onSetAsDefault: (enhancedRef: EnhancedGitRef) => void,
    onShowSecurityDialog: (branchName: string) => void,
    getBranchPolicyUrl?: (branchName: string) => string,
    tableRef?: React.RefObject<Table<EnhancedGitRef> | Tree<EnhancedGitRef>>
) {
    return new ColumnMore<UniversalBranchColumnContent>((treeItem, rowIndex, buttonRef) => {
        const columnGitRef = getColumnGitRef(treeItem);
        const items: IMenuItem[] = [];

        if (columnGitRef.gitRef) {
            const gitRef = columnGitRef.gitRef;

            // First group: Create Branch, Create PR, Delete Branch, Divider (there's always items in group 2)
            const canCreateBranch = onCheckPermission(GitRepositoryPermissions.CreateBranch);
            const canSetDefaultBranch = onCheckPermission(GitRepositoryPermissions.EditPolicies);
            const canCreatePullRequest = onCheckPermission(GitRepositoryPermissions.PullRequestContribute, gitRef.rootRef);
            const canDeleteBranch = isUserMember;

            const returnFocusOnDismiss = async () => {
                // a11y: Restore focus to the more-button after we dismiss any dialog (create branch, create tag)
                // First we need to ensure row is focused. This is because while loading a dialog async'ly,
                // it uses to lose focus, so button more gets hidden
                await tableRef?.current?.focusRow(rowIndex);
                buttonRef.current?.focus();
            };

            if (canCreateBranch) {
                items.push({
                    id: "newBranch",
                    text: Resources.CreateNewBranch,
                    iconProps: { iconName: "Add" },
                    onActivate: () => onCreate(gitRef, returnFocusOnDismiss),
                    rank: 10
                });
            }

            if (canCreatePullRequest) {
                items.push({
                    id: "newPR",
                    text: Resources.NewPullRequest,
                    iconProps: { iconName: "BranchPullRequest" },
                    disabled: gitRef.createPullRequestUrl === undefined,
                    href: gitRef.createPullRequestUrl,
                    rank: 20
                });
            }

            if (canDeleteBranch) {
                items.push({
                    id: "delete",
                    text: Resources.DeleteBranch,
                    iconProps: { iconName: "Delete" },
                    onActivate: () => onDelete(gitRef, returnFocusOnDismiss),
                    rank: 30
                });
            }

            if (canCreateBranch || canCreatePullRequest || canDeleteBranch) {
                items.push({
                    id: "divderOne",
                    itemType: MenuItemType.Divider,
                    rank: 40
                });
            }

            // Second group: View Files, History, Compare Branches, Set as Compare
            // These are always enabled on the old view
            items.push({
                id: "viewFiles",
                text: Resources.ViewFiles,
                iconProps: { iconName: "FileCode" },
                href: gitRef.refUrl,
                rank: 50
            });

            items.push({
                id: "history",
                text: Resources.ViewHistory,
                iconProps: { iconName: "History" },
                href: gitRef.historyUrl,
                rank: 60
            });

            items.push({
                id: "compareBranches",
                text: Resources.CompareBranches,
                iconProps: { iconName: "DiffSideBySide" },
                disabled: gitRef.isCompare || gitRef.compareUrl === undefined,
                href: gitRef.isCompare ? undefined : gitRef.compareUrl, // If we don't set to undefined here, it doesn't display as disabled properly
                rank: 70
            });

            items.push({
                id: "setAsCompareBranch",
                text: Resources.SetAsCompareBranch,
                iconProps: { iconName: "BranchCompare" },
                disabled: gitRef.isCompare,
                onActivate: () => onSetAsCompare(gitRef),
                rank: 80
            });

            if (canSetDefaultBranch) {
                items.push({
                    id: "setAsDefaultBranch",
                    text: Resources.SetAsDefaultBranch,
                    iconProps: { iconName: "OpenSource" },
                    disabled: gitRef.isDefault,
                    onActivate: () => onSetAsDefault(gitRef),
                    rank: 90
                });
            }

            // Third group: Divider, lock/unlock, policy, security
            if (isUserMember) {
                items.push({
                    id: "dividerTwo",
                    itemType: MenuItemType.Divider,
                    rank: 100
                });

                if (gitRef.rootRef.isLocked) {
                    items.push({
                        id: "unlock",
                        text: Resources.UnlockBranch,
                        iconProps: { iconName: "Unlock" },
                        onActivate: () => onUnlock(gitRef),
                        rank: 110
                    });
                } else {
                    items.push({
                        id: "lock",
                        text: Resources.LockBranch,
                        iconProps: { iconName: "Lock" },
                        onActivate: () => onLock(gitRef),
                        rank: 120
                    });
                }

                items.push({
                    id: "policies",
                    text: Resources.BranchPolicies,
                    iconProps: { iconName: "Ribbon" },
                    href: gitRef.policiesUrl,
                    rank: 130
                });

                items.push({
                    id: "security",
                    text: Resources.BranchSecurity,
                    iconProps: { iconName: "Shield" },
                    onActivate: () => onShowSecurityDialog(columnGitRef.longName),
                    rank: 140
                });
            }
        } else {
            // Add the "/*" to make it a folder
            const refsHeadsIdentifier = branchNameToRef(columnGitRef.longName) + "/*";

            if (isUserMember) {
                items.push({
                    id: "policies",
                    text: Resources.BranchPolicies,
                    iconProps: { iconName: "Ribbon" },
                    disabled: getBranchPolicyUrl === undefined,
                    href: getBranchPolicyUrl ? getBranchPolicyUrl(refsHeadsIdentifier) : undefined,
                    rank: 10
                });
            }
        }

        const collection = new ObservableCollection<IMenuItem>();
        collection.push(items);
        addContributedMenuItems(columnGitRef.shortName, columnGitRef.gitRef, collection);
        return {
            id: "tree-action-menu",
            items: collection
        };
    });
}

function calcBarWidth(commitCount: number) {
    let style = "0";
    let barLength = 0;

    // We allow a max of 40px and calculate (log base 10(# of commits) + 1) * 8 for a weighted distribution
    if (commitCount) {
        barLength = (Math.log(commitCount) / 2.303 + 1) * 8;
        barLength = barLength > 40 ? 40 : barLength;
    }
    style = barLength + "px";
    return style;
}

interface IBehindAheadProps {
    className?: string;
    compareBranch?: EnhancedGitRef;
    compareUrl?: string;
    reverseCompareUrl?: string;
    stats: GitBranchStats;
}

const BehindAhead: React.FunctionComponent<IBehindAheadProps> = props => {
    const { className, compareBranch, compareUrl, reverseCompareUrl, stats } = props;

    const behindWidth: string = calcBarWidth(stats.behindCount);
    const aheadWidth: string = calcBarWidth(stats.aheadCount);
    const compareBranchName = compareBranch ? refToBranchName(compareBranch.rootRef.name) : Resources.TooltipBehindAheadBranchNameFallback;
    const tooltipText = format(Resources.TooltipBehindAhead, stats.behindCount, stats.aheadCount, compareBranchName);

    const aheadLabel = format(Resources.AriaLabelAhead, stats.aheadCount, compareBranchName);
    const behindLabel = format(Resources.AriaLabelBehind, stats.aheadCount, compareBranchName);

    return (
        <Tooltip text={tooltipText}>
            <div className={css(className, "flex-row flex-center")}>
                <div className="repos-behind-column flex-column">
                    <FPSLink
                        ariaLabel={behindLabel}
                        className="repos-behind-link bolt-table-link flex-self-end flex-column"
                        href={reverseCompareUrl}
                        removeUnderline={true}
                    >
                        <span className="repos-behind-text body-s">{stats.behindCount}</span>
                        <div className="repos-behind-bar flex-self-end" style={{ width: behindWidth }} />
                    </FPSLink>
                </div>
                <div className="repos-behind-ahead-divider flex-self-stretch" />
                <div className="repos-ahead-column flex-column">
                    <FPSLink
                        ariaLabel={aheadLabel}
                        className="repos-ahead-link bolt-table-link flex-self-start flex-column"
                        href={compareUrl}
                        removeUnderline={true}
                    >
                        <span className="repos-ahead-text body-s">{stats.aheadCount}</span>
                        <div className="repos-ahead-bar" style={{ width: aheadWidth }} />
                    </FPSLink>
                </div>
            </div>
        </Tooltip>
    );
};